import React, { useState, useEffect } from "react";
import { useCart } from "../context/cartContext";
import { useUser } from "../context/userContext";
import { useNavigate } from "react-router-dom";
import logo_light from "./logooo-tttt_light.png";
import logo_dark from "./logooo-tttt.png";

const Header = () => {
  const [isCartDropdownVisible, setIsCartDropdownVisible] = useState(false);
  const [isUserDropdownVisible, setIsUserDropdownVisible] = useState(false);
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { cartItems, calculateTotal, removeFromCart } = useCart();
  const { user, setUser } = useUser(); // Assuming `setUser` is available to update user state
  const navigate = useNavigate();

  // State to force re-render
  const [loggedOut, setLoggedOut] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null); // Clear user state in context
    setLoggedOut(true); // Set loggedOut state to trigger re-render

    // Check if the user is already on the homepage
    if (window.location.pathname === "/") {
      window.location.reload(); // Refresh the page if already on the homepage
    } else {
      navigate("/"); // Redirect to homepage if not on the homepage
    }
  };

  useEffect(() => {
    if (loggedOut) {
      // Reset loggedOut state after logout
      setLoggedOut(false);
    }
  }, [loggedOut]);

  const totalCartQuantity = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <nav className="bg-white dark:bg-gray-800 antialiased sticky top-0 z-50 shadow-md">
      <div className="px-4 mx-auto py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-8">
            <div className="shrink-0">
              <a href="/" title="Home">
                <img
                  className="block w-auto h-16 w-24 dark:hidden"
                  src={logo_dark}
                  alt="Logo"
                />
                <img
                  className="hidden w-auto h-16 w-24 dark:block"
                  src={logo_light}
                  alt="Dark Logo"
                />
              </a>
            </div>
          </div>

          {/* Burger Icon for Mobile */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuVisible(!isMobileMenuVisible)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white focus:outline-none"
            >
              <i className="fas fa-bars text-2xl"></i>
            </button>
          </div>

          {/* Desktop Menu */}
          <ul className="hidden lg:flex items-center justify-start gap-6 py-3">
            {["Home", "All Categories", "Second Hand", "Pricing", "Contact", "About us"].map(
              (link, index) => (
                <li key={index}>
                  <a
                    href={link === "Home" ? "/" : `/${link.toLowerCase().replace(" ", "-")}`}
                    title={link}
                    className="flex text-sm font-medium text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400 transition"
                  >
                    {link}
                  </a>
                </li>
              )
            )}
          </ul>

          {/* Desktop Search */}
          <form
            onSubmit={handleSearchSubmit}
            className="hidden lg:flex items-center w-full max-w-xs mx-4"
          >
            <div className="relative w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                placeholder="Search..."
              />
              <button
                type="submit"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 dark:text-gray-400"
              >
                <i className="fas fa-search"></i>
              </button>
            </div>
          </form>

          {/* Cart and User Dropdown */}
          <div className="flex items-center space-x-4">
            {/* Cart */}
            <div
              onMouseEnter={() => setIsCartDropdownVisible(true)}
              onMouseLeave={() => setIsCartDropdownVisible(false)}
              className="relative"
            >
              <a
                href="/cart"
                type="button"
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-[#1D3351] dark:text-white rounded-full transition"
              >
                <i className="fas fa-shopping-cart w-5 h-5 relative">
                  {totalCartQuantity > 0 && (
                    <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
                      {totalCartQuantity}
                    </span>
                  )}
                </i>
              </a>

              {isCartDropdownVisible && (
                <div className="absolute right-0 z-10 w-80 p-5 bg-white rounded-lg shadow-lg dark:bg-gray-800">
                  {cartItems.length > 0 ? (
                    <div className="space-y-4">
                      {cartItems.map((item) => {
                        const parsedImages = JSON.parse(item.images);
                        const imageUrl = `https://api.theonlysale.com/uploads/images/${parsedImages[0]
                          .split(/[\\/]/)
                          .pop()}`;

                        return (
                          <div
                            key={item.id}
                            className="grid grid-cols-2 gap-4 border-b pb-4 last:border-none"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="h-16 w-16 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                <img
                                  src={imageUrl}
                                  alt={item.name}
                                  className="h-full w-full object-cover rounded-lg"
                                />
                              </div>
                              <div>
                                <a
                                  href="#"
                                  className="truncate text-sm font-semibold text-gray-900 dark:text-white hover:underline"
                                >
                                  {item.name}
                                </a>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                  ${item.final_price} x {item.quantity}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end justify-between">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                ${item.final_price * item.quantity}
                              </p>
                              <button
                                onClick={() => removeFromCart(item.id)}
                                className="text-sm text-red-600 hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <div className="pt-4">
                        <div className="flex justify-between text-sm font-semibold text-gray-900 dark:text-white">
                          <span>Total</span>
                          <span>${calculateTotal()}</span>
                        </div>
                        <a
                          href="/checkout"
                          className="mt-4 block w-full text-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition"
                        >
                          Proceed to Checkout
                        </a>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Your cart is empty.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Wishlist */}
            <a
              href="/wishlist"
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-[#1D3351] dark:text-white rounded-full transition"
            >
              <i className="fas fa-heart w-5 h-5"></i>
            </a>

            {/* User Dropdown */}
            {user ? (
              <div
                onMouseEnter={() => setIsUserDropdownVisible(true)}
                onMouseLeave={() => setIsUserDropdownVisible(false)}
                className="relative"
              >
                <a
                  href="#"
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-[#1D3351] dark:text-white rounded-full transition"
                >
                  <i className="fas fa-user w-5 h-5"></i>
                  {user.name}
                </a>

                {isUserDropdownVisible && (
                  <div className="absolute right-0 z-10 w-48 p-4 bg-white rounded-lg shadow-lg dark:bg-gray-800">
                    <a
                      href="/profile"
                      className="block text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded transition"
                    >
                      My Profile
                    </a>
                 
                    <button
                      onClick={handleLogout}
                      className="w-full text-left text-sm font-semibold text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded transition"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <a
                href="/login"
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-[#1D3351] dark:text-white rounded-full transition"
              >
                <i className="fas fa-sign-in-alt w-5 h-5"></i>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuVisible && (
        <div className="lg:hidden">
          <div className="bg-white dark:bg-gray-800 p-4">
            <ul className="space-y-4">
              {["Home", "All Categories", "Second Hand", "Pricing", "Contact", "About us"].map(
                (link, index) => (
                  <li key={index}>
                    <a
                      href={link === "Home" ? "/" : `/${link.toLowerCase().replace(" ", "-")}`}
                      title={link}
                      className="flex text-sm font-medium text-gray-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400 transition"
                    >
                      {link}
                    </a>
                  </li>
                )
              )}
            </ul>
            <form onSubmit={handleSearchSubmit} className="mt-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                placeholder="Search..."
              />
            </form>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
